import { useEffect, useState } from 'react';
import { api } from '../lib/api';

/**
 * Platform settings. Three configurable email addresses that drive
 * outbound mail destinations + Reply-To headers across the whole
 * stack:
 *
 *   - sys_admin_email: where ops failure alerts go (worker failures,
 *     uptime transitions, etc).
 *   - sales_email: surfaced as the sales contact on the public
 *     signup site and as Reply-To on the trial-ending email.
 *   - support_email: Reply-To on all customer-facing email
 *     (welcome, password reset, payment failed, receipts).
 *
 * Values are stored in admin_audit_log on each save so we can see who
 * changed what later.
 */

interface SettingsResp {
  settings: Record<string, string | null>;
}

const FIELDS: Array<{
  key: 'sys_admin_email' | 'sales_email' | 'support_email';
  label: string;
  help: string;
  placeholder: string;
}> = [
  {
    key: 'sys_admin_email',
    label: 'System admin email',
    help:
      "Failure alerts (provisioning errors, uptime monitor transitions). Goes to the team, not customers.",
    placeholder: 'ops@example.com',
  },
  {
    key: 'sales_email',
    label: 'Sales email',
    help:
      'Customer-facing sales contact. Shown on the public signup site footer and used as Reply-To on trial-ending emails.',
    placeholder: 'sales@example.com',
  },
  {
    key: 'support_email',
    label: 'Support email',
    help:
      'Customer-facing support contact. Used as Reply-To on every transactional email a customer receives (welcome, password reset, payment failed, receipt).',
    placeholder: 'support@example.com',
  },
];

export function Settings() {
  const [state, setState] = useState<Record<string, string>>({
    sys_admin_email: '',
    sales_email: '',
    support_email: '',
  });
  const [initial, setInitial] = useState<Record<string, string>>(state);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      const data = await api<SettingsResp>('/admin/settings');
      const merged: Record<string, string> = { ...state };
      for (const f of FIELDS) merged[f.key] = (data.settings[f.key] as string) ?? '';
      setState(merged);
      setInitial(merged);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  function dirtyKeys(): string[] {
    return FIELDS
      .map((f) => f.key)
      .filter((k) => (state[k] ?? '') !== (initial[k] ?? ''));
  }
  const hasChanges = dirtyKeys().length > 0;

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!hasChanges) return;
    setBusy(true);
    setInfo(null);
    setError(null);
    try {
      const patch: Record<string, string> = {};
      for (const k of dirtyKeys()) patch[k] = state[k]!.trim();
      const data = await api<SettingsResp>('/admin/settings', { method: 'PATCH', json: patch });
      const merged: Record<string, string> = { ...state };
      for (const f of FIELDS) merged[f.key] = (data.settings[f.key] as string) ?? '';
      setState(merged);
      setInitial(merged);
      setInfo(`Updated: ${Object.keys(patch).join(', ')}`);
    } catch (e) {
      const errBody = e as Error & { body?: { error?: string } };
      setError(errBody.body?.error || errBody.message || 'Save failed');
    } finally {
      setBusy(false);
    }
  }

  function reset() {
    setState(initial);
    setInfo(null);
    setError(null);
  }

  // Test-send: prompts the server to deliver a small confirmation email to
  // whatever value is currently STORED at that key. Refuses if the field
  // is dirty — the persisted value is what gets mailed, so we avoid
  // confusion ('I changed it but the test went to the OLD address').
  async function testSend(key: typeof FIELDS[number]['key']) {
    if (state[key] !== initial[key]) {
      setError(`Save your changes to "${key}" first — test send uses the stored value.`);
      return;
    }
    if (!initial[key] || !initial[key]!.trim()) {
      setError(`No value stored for "${key}" yet.`);
      return;
    }
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      const resp = await api<{ sent_to: string }>('/admin/settings/test-send', {
        method: 'POST',
        json: { key },
      });
      setInfo(`Test email queued to ${resp.sent_to}. Check the inbox (and spam) in a few seconds.`);
    } catch (e) {
      const errBody = e as Error & { body?: { error?: string; detail?: string } };
      setError(errBody.body?.error || errBody.body?.detail || errBody.message || 'Test send failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <h1>Platform settings</h1>
      <p className="subtitle">
        Email addresses the platform uses for ops alerts and customer
        communication. Change any value here and the next email send
        picks it up (cached for ~30s).
      </p>
      {error && <div className="banner err">{error}</div>}
      {info && <div className="banner ok">{info}</div>}

      {loading ? (
        <div className="muted">Loading…</div>
      ) : (
        <form onSubmit={save} className="card" style={{ maxWidth: 720 }}>
          {FIELDS.map((f) => {
            const dirty = (state[f.key] ?? '') !== (initial[f.key] ?? '');
            const hasStored = !!(initial[f.key] && initial[f.key]!.trim());
            return (
              <div key={f.key} style={{ marginBottom: 18 }}>
                <label htmlFor={f.key}>{f.label}</label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                  <input
                    id={f.key}
                    type="email"
                    value={state[f.key] ?? ''}
                    onChange={(e) => setState({ ...state, [f.key]: e.target.value })}
                    placeholder={f.placeholder}
                    disabled={busy}
                    style={{ flex: 1 }}
                  />
                  <button
                    type="button"
                    className="secondary"
                    disabled={busy || dirty || !hasStored}
                    title={
                      dirty
                        ? 'Save your changes first — test send uses the stored value.'
                        : !hasStored
                        ? 'No value stored yet.'
                        : 'Send a one-line test email to confirm the address works.'
                    }
                    onClick={() => testSend(f.key)}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    ✉ Test send
                  </button>
                </div>
                <div className="muted small" style={{ marginTop: 4 }}>{f.help}</div>
              </div>
            );
          })}
          <div className="button-row" style={{ marginTop: 12 }}>
            <button type="submit" disabled={busy || !hasChanges}>
              {busy ? 'Saving…' : hasChanges ? `Save ${dirtyKeys().length} change${dirtyKeys().length === 1 ? '' : 's'}` : 'Saved'}
            </button>
            {hasChanges && (
              <button type="button" className="secondary" disabled={busy} onClick={reset}>
                Discard
              </button>
            )}
          </div>
        </form>
      )}
    </div>
  );
}
